/**
 * Fetch wiring for DashboardPage (E2.1 design spec section 4): one
 * useCallback + useLoadState pair per GET call (current/previous summary,
 * timeseries, by-shop, revenue, previous revenue, and the day-capped
 * current summary/revenue the month-to-date delta chips compare -- see
 * periodState.ts's computeComparisonContext). Split out of
 * DashboardPage.tsx purely to keep that component's cognitive complexity
 * down (SonarQube S3776). PERF refetch-scoping contract (spec 11.5 +
 * chart-filter follow-up): shop/metric changes refetch ONLY timeseries,
 * and WITHIN compare mode changing which shops are ticked refetches
 * nothing at all -- fetchTimeseries's identity derives from compareMode +
 * the single-shop id, never the raw chartShopIds array (the group_by=shop
 * request already returns every shop; ticking a 3rd shop only changes
 * client-side filtering). Entering/leaving compare mode or changing the
 * single selected shop still refetches, since those change the request
 * params themselves. Past the hook size budget by design: every dashboard
 * GET lives here so the perf contract (one mount, N requests) is auditable
 * in one place.
 */
import { useCallback } from 'react';

import type { Api, ByShopResponse, ReportsSummaryResponse, RevenueResult, TimeseriesResult } from '../../api/index.ts';
import { useLoadState, type UseLoadStateResult } from '../../hooks/useLoadState.ts';
import type { Period } from './periodState.ts';

const LIVE_ONLY = 'live';

// The EMPTY_* initial values below are exported (not just EMPTY_SUMMARY) so
// DashboardPage's first-load skeleton flags can use REFERENCE equality: a
// genuinely-empty SERVER response is a fresh object, so `data === EMPTY_X`
// goes false forever once the first load lands, where a data-emptiness
// check would re-flash skeletons on every later reload for a tenant with
// an empty roster / empty revenue period.
export const EMPTY_SUMMARY: ReportsSummaryResponse = { tax_basis: 'gross_profit', tax_rate_bp: 0, by_currency: [] };
const EMPTY_TIMESERIES: TimeseriesResult = { year: new Date().getFullYear(), tax_basis: 'gross_profit', tax_rate_bp: 0, months: [] };
export const EMPTY_BY_SHOP: ByShopResponse = { shops: [] };
export const EMPTY_REVENUE: RevenueResult = { commission_rate_bp: 0, device_monthly_fee_minor: 0, months_in_period: 1, by_shop: [], totals: [] };

export interface DashboardFetchersParams {
  api: Api;
  year: number;
  effectiveMonth: number | undefined;
  prev: Period;
  /** ComparisonContext.toDay: when non-null, the previous-period calls AND
   *  the capped current-side chip-basis calls all carry it. */
  toDay: number | null;
  /** ComparisonContext.comparable: false (the 1st of the current month)
   *  skips the previous-period calls entirely -- no chips will render. */
  comparable: boolean;
  chartShopIds: readonly number[];
  /** ROADMAP:40's population selection. Ignored without reports:write --
   *  every call is clamped to live below, so a stale stored selection can
   *  never influence a non-writer's fetches. */
  reportTypes: readonly string[];
  canReadShops: boolean;
  canReadRevenue: boolean;
  showInternalDetail: boolean;
}

export interface DashboardFetchers {
  currentSummary: ReportsSummaryResponse;
  currentState: UseLoadStateResult<ReportsSummaryResponse>['state'];
  reloadCurrent: UseLoadStateResult<ReportsSummaryResponse>['reload'];
  previousSummary: ReportsSummaryResponse;
  previousState: UseLoadStateResult<ReportsSummaryResponse>['state'];
  reloadPrevious: UseLoadStateResult<ReportsSummaryResponse>['reload'];
  timeseriesResult: TimeseriesResult;
  timeseriesState: UseLoadStateResult<TimeseriesResult>['state'];
  reloadTimeseries: UseLoadStateResult<TimeseriesResult>['reload'];
  byShop: ByShopResponse;
  byShopState: UseLoadStateResult<ByShopResponse>['state'];
  reloadByShop: UseLoadStateResult<ByShopResponse>['reload'];
  revenue: RevenueResult;
  revenueState: UseLoadStateResult<RevenueResult>['state'];
  reloadRevenue: UseLoadStateResult<RevenueResult>['reload'];
  previousRevenue: RevenueResult;
  previousRevenueState: UseLoadStateResult<RevenueResult>['state'];
  reloadPreviousRevenue: UseLoadStateResult<RevenueResult>['reload'];
  /** Current period capped through toDay -- the chip basis when toDay is
   *  non-null. EMPTY_SUMMARY (never fetched) when toDay is null. */
  cappedCurrentSummary: ReportsSummaryResponse;
  cappedCurrentState: UseLoadStateResult<ReportsSummaryResponse>['state'];
  reloadCappedCurrent: UseLoadStateResult<ReportsSummaryResponse>['reload'];
  /** Revenue twin of cappedCurrentSummary; EMPTY_REVENUE when toDay is null. */
  cappedCurrentRevenue: RevenueResult;
  cappedCurrentRevenueState: UseLoadStateResult<RevenueResult>['state'];
  reloadCappedCurrentRevenue: UseLoadStateResult<RevenueResult>['reload'];
}

/** Every GET call DashboardPage composes, wired to useLoadState. See the
 *  module docstring above for why this lives in its own file. */
export function useDashboardFetchers({
  api,
  year,
  effectiveMonth,
  prev,
  toDay,
  comparable,
  chartShopIds,
  reportTypes,
  canReadShops,
  canReadRevenue,
  showInternalDetail,
}: Readonly<DashboardFetchersParams>): DashboardFetchers {
  // A plain string, so every fetcher's useCallback identity stays stable
  // across renders that only rebuild the selection array.
  const reportType = showInternalDetail && reportTypes.length > 0 ? [...reportTypes].join(',') : LIVE_ONLY;

  const fetchCurrent = useCallback(
    () => api.reports.summary({ year, month: effectiveMonth, report_type: reportType }),
    [api, year, effectiveMonth, reportType],
  );
  const { data: currentSummary, state: currentState, reload: reloadCurrent } = useLoadState(fetchCurrent, EMPTY_SUMMARY);

  const fetchPrevious = useCallback(
    () =>
      comparable
        ? api.reports.summary({ year: prev.year, month: prev.month ?? undefined, report_type: reportType, to_day: toDay ?? undefined })
        : Promise.resolve(EMPTY_SUMMARY),
    [api, comparable, prev.year, prev.month, toDay, reportType],
  );
  const { data: previousSummary, state: previousState, reload: reloadPrevious } = useLoadState(fetchPrevious, EMPTY_SUMMARY);

  // toDay is non-null only for a comparable current month, which always has
  // effectiveMonth set (whole-year mode never caps).
  const fetchCappedCurrent = useCallback(
    () =>
      toDay === null
        ? Promise.resolve(EMPTY_SUMMARY)
        : api.reports.summary({ year, month: effectiveMonth, report_type: reportType, to_day: toDay }),
    [api, year, effectiveMonth, toDay, reportType],
  );
  const {
    data: cappedCurrentSummary,
    state: cappedCurrentState,
    reload: reloadCappedCurrent,
  } = useLoadState(fetchCappedCurrent, EMPTY_SUMMARY);

  const compareMode = chartShopIds.length >= 2;
  // Deliberately NOT the raw chartShopIds array in the deps below: in
  // compare mode the request is group_by=shop for ALL shops (which shops
  // are ticked only filters client-side, in buildShopCompareSeries), so
  // re-ticking within compare mode must not re-run an identical fetch.
  // Only the two values that actually change the request params --
  // compareMode itself and the single-shop id -- feed the identity.
  const singleShopId = compareMode ? undefined : chartShopIds[0];
  // showInternalDetail here is a WASTED-REQUEST optimization, not a data gate:
  // the money chart is the only consumer and it doesn't render without
  // reports:write, but reports:read legitimately serves /reports/timeseries.
  const fetchTimeseries = useCallback(() => {
    if (!showInternalDetail) return Promise.resolve(EMPTY_TIMESERIES);
    if (compareMode) {
      return api.reports.timeseries({ year, group_by: 'shop', report_type: reportType });
    }
    return api.reports.timeseries({ year, shop_id: singleShopId, report_type: reportType });
  }, [api, year, compareMode, singleShopId, showInternalDetail, reportType]);
  const { data: timeseriesResult, state: timeseriesState, reload: reloadTimeseries } = useLoadState(fetchTimeseries, EMPTY_TIMESERIES);

  // Shop roster (E2.1 owner privacy ruling 2026-07-14) is gated the same way
  // as revenue below -- without shops:read the fetcher never calls
  // api.reports.byShop at all, so an External/government session leaves zero
  // trace of the shop roster on the wire.
  const fetchByShop = useCallback(
    () => (canReadShops ? api.reports.byShop({ year, month: effectiveMonth, report_type: reportType }) : Promise.resolve(EMPTY_BY_SHOP)),
    [api, canReadShops, year, effectiveMonth, reportType],
  );
  const { data: byShop, state: byShopState, reload: reloadByShop } = useLoadState(fetchByShop, EMPTY_BY_SHOP);

  // Revenue (E2.1 spec 7.5/7.6) is PRIVATE 1NL business data -- without
  // revenue:read the fetcher never calls api.reports.revenue at all (not
  // just "the result is hidden"), so an External session leaves zero trace
  // of it on the wire. The hook itself is still called unconditionally
  // (rules of hooks); only the network call inside is gated.
  const fetchRevenue = useCallback(
    () => (canReadRevenue ? api.reports.revenue({ year, month: effectiveMonth, report_type: reportType }) : Promise.resolve(EMPTY_REVENUE)),
    [api, canReadRevenue, year, effectiveMonth, reportType],
  );
  const { data: revenue, state: revenueState, reload: reloadRevenue } = useLoadState(fetchRevenue, EMPTY_REVENUE);

  const fetchPreviousRevenue = useCallback(
    () =>
      canReadRevenue && comparable
        ? api.reports.revenue({ year: prev.year, month: prev.month ?? undefined, report_type: reportType, to_day: toDay ?? undefined })
        : Promise.resolve(EMPTY_REVENUE),
    [api, canReadRevenue, comparable, prev.year, prev.month, toDay, reportType],
  );
  const {
    data: previousRevenue,
    state: previousRevenueState,
    reload: reloadPreviousRevenue,
  } = useLoadState(fetchPreviousRevenue, EMPTY_REVENUE);

  const fetchCappedCurrentRevenue = useCallback(
    () =>
      canReadRevenue && toDay !== null
        ? api.reports.revenue({ year, month: effectiveMonth, report_type: reportType, to_day: toDay })
        : Promise.resolve(EMPTY_REVENUE),
    [api, canReadRevenue, year, effectiveMonth, toDay, reportType],
  );
  const {
    data: cappedCurrentRevenue,
    state: cappedCurrentRevenueState,
    reload: reloadCappedCurrentRevenue,
  } = useLoadState(fetchCappedCurrentRevenue, EMPTY_REVENUE);

  return {
    currentSummary,
    currentState,
    reloadCurrent,
    previousSummary,
    previousState,
    reloadPrevious,
    timeseriesResult,
    timeseriesState,
    reloadTimeseries,
    byShop,
    byShopState,
    reloadByShop,
    revenue,
    revenueState,
    reloadRevenue,
    previousRevenue,
    previousRevenueState,
    reloadPreviousRevenue,
    cappedCurrentSummary,
    cappedCurrentState,
    reloadCappedCurrent,
    cappedCurrentRevenue,
    cappedCurrentRevenueState,
    reloadCappedCurrentRevenue,
  };
}

export default useDashboardFetchers;
